import React, { useState } from "react";

import { PlayCircleOutline as PlayCircleOutlineIcon } from "@material-ui/icons";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { useParams } from "react-router-dom";

import FastAPI from "../../../api/FastAPI";
import { getVideoType } from "../../../utils/videos";
import Player, { convertCuepointsToMarkers } from "../../player/Player";

import type { JSX } from "react";

import type { Schedule } from "../../../api/FastAPI.types";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

interface Props {
  isLive?: boolean;
  schedule?: Schedule;
  uuid: string;
  poster?: string;
}

function VideoOverview({ schedule, isLive, uuid, poster }: Props): JSX.Element {
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const [isPlaying, setIsPlaying] = useState(isLive);

  const handlePlayClick = (): void => {
    setIsPlaying(true);
  };

  const getSchedulePreview = FastAPI.generateQuery({
    fn: FastAPI.getSchedulePreview,
    params: [{ channelId, scheduleId: schedule?.uuid ?? "", uuid }],
  });

  const { data: schedulePreview } = useQuery({
    ...getSchedulePreview,
    enabled: !isLive && isPlaying,
  });

  const source = isLive ? schedule?.hls_url : schedulePreview?.url;

  const type = getVideoType(source ?? "");

  const playerParams = {
    source: source ?? "",
    type: type ?? undefined,
    markers: convertCuepointsToMarkers(schedulePreview?.cuepoints ?? []),
    poster,
  };

  const isPlayerReady = source && type;

  const iconClassNames = classNames(
    "flex cursor-pointer items-center justify-center overflow-hidden transition duration-500 group-hover:scale-110 bg-black rounded-full text-overlay-foreground p-[0.3125rem] bg-opacity-80",
  );

  return (
    <div className="my-[0.78125rem] flex h-[300px] w-full items-center justify-center overflow-hidden rounded-[0.3125rem] bg-black drop-shadow-md">
      {!isPlaying ? (
        <div
          className="group flex size-full items-center justify-center bg-cover bg-center bg-no-repeat"
          onClick={handlePlayClick}
          onKeyDown={handlePlayClick}
          role="button"
          tabIndex={0}
          style={{
            backgroundImage: `url(${poster})`,
          }}
        >
          <div className={iconClassNames}>
            <PlayCircleOutlineIcon style={{ fontSize: "5rem" }} />
          </div>
        </div>
      ) : (
        isPlayerReady && <Player isFullSize playerParams={playerParams} />
      )}
    </div>
  );
}

export default VideoOverview;
